// src/pages/Docket-page/DocketConfirmationPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdEdit,
  MdDelete,
  MdSearch,
  MdClear,
  MdRefresh,
  MdVisibility,
  MdFileDownload,
  MdFilterList,
  MdCheckCircle,
  MdPendingActions,
  MdLocalShipping
} from 'react-icons/md';
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineDocumentText,
  HiOutlineScale,
  HiOutlineCurrencyRupee
} from 'react-icons/hi';
import {
  FaFilePdf,
  FaPlus,
  FaMinus,
  FaExchangeAlt,
  FaTimes,
  FaBoxes,
  FaCheckCircle
} from 'react-icons/fa';
import { generateDocketPDF } from '../../utils/generateDocketPDF';
import BASE_URL from '../../api';

const API_BASE = `${BASE_URL}/api`;

const DocketConfirmationPage = () => {
  const token = localStorage.getItem('token');
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerPrices, setCustomerPrices] = useState([]);

  // View & Pagination
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modeFilter, setModeFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editDocket, setEditDocket] = useState(null);
  const [formData, setFormData] = useState({});
  const [measurements, setMeasurements] = useState([]);
  const [viewDocket, setViewDocket] = useState(null);

  // ────── FETCH CUSTOMER PRICE SETTINGS ──────
  const fetchCustomerPrices = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/master/customerpricesetting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.data && Array.isArray(json.data)) {
        setCustomerPrices(json.data);
      } else if (Array.isArray(json)) {
        setCustomerPrices(json);
      }
    } catch (err) {
      console.error('Error fetching customer price settings:', err);
    }
  };

  // Helper: Get Volume Factor from Customer Rate Master or fallback to 4500
  const getVolumeFactor = (docket) => {
    if (!docket) return 4500;
    const customerId =
      docket.consigneeid?._id ||
      docket.consigneeid ||
      docket.clientid?._id ||
      docket.clientid ||
      docket.consignorid?._id ||
      docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && Number(match.volume) > 0) {
        return Number(match.volume);
      }
    }
    if (Number(docket.volumeFactor) > 0) {
      return Number(docket.volumeFactor);
    }
    return 4500;
  };

  // Helper: Get Origin Location
  const getOriginLocation = (docket) => {
    if (!docket) return 'N/A';
    const customerId =
      docket.consigneeid?._id ||
      docket.consigneeid ||
      docket.clientid?._id ||
      docket.clientid ||
      docket.consignorid?._id ||
      docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && match.fromLocationId) {
        if (Array.isArray(match.fromLocationId) && match.fromLocationId.length > 0) {
          const names = match.fromLocationId
            .map(l => l?.areaname || l?.hub || l?.name || (typeof l === 'string' && !/^[0-9a-fA-F]{24}$/.test(l) ? l : ''))
            .filter(Boolean);
          if (names.length > 0) return names.join(', ');
        } else if (typeof match.fromLocationId === 'object') {
          const name = match.fromLocationId.areaname || match.fromLocationId.hub || match.fromLocationId.name;
          if (name) return name;
        }
      }
    }
    return docket.customerbranch?.branchname || docket.originlocation?.areaname || docket.originlocation?.hub || docket.origin || 'N/A';
  };

  // Helper: Get Destination Location
  const getDestLocation = (docket) => {
    if (!docket) return 'N/A';
    const customerId =
      docket.consigneeid?._id ||
      docket.consigneeid ||
      docket.clientid?._id ||
      docket.clientid ||
      docket.consignorid?._id ||
      docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && match.toLocationId) {
        if (Array.isArray(match.toLocationId) && match.toLocationId.length > 0) {
          const names = match.toLocationId
            .map(l => l?.areaname || l?.hub || l?.name || (typeof l === 'string' && !/^[0-9a-fA-F]{24}$/.test(l) ? l : ''))
            .filter(Boolean);
          if (names.length > 0) return names.join(', ');
        } else if (typeof match.toLocationId === 'object') {
          const name = match.toLocationId.areaname || match.toLocationId.hub || match.toLocationId.name;
          if (name) return name;
        }
      }
    }
    return docket.destlocation?.areaname || docket.destlocation?.hub || docket.destination || 'N/A';
  };

  const getModeLabel = (mode) => {
    if (mode === 1) return 'SURFACE';
    if (mode === 2) return 'AIR';
    if (mode === 3) return 'TRAIN';
    return 'STANDARD';
  };

  // ────── FETCH DOCKETS ──────
  const fetchDockets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/getalldocketconfirmation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setDockets(json.data || []);
      } else {
        toast.error(json.message || 'Failed to load dockets');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while fetching dockets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDockets();
      fetchCustomerPrices();
    }
  }, [token]);

  const isSuperAdmin = () => {
    const role = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
    return role.includes('super admin') || role.includes('superadmin') || role.includes('super_admin') || role === 'admin';
  };

  // ────── DELETE DOCKET ──────
  const handleDelete = async (id, docketNo) => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin can delete dockets');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Docket Confirmation?',
      text: `Are you sure you want to delete docket "${docketNo || id}" permanently?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      borderRadius: '0.75rem',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE}/onlinebookings/docketconfirmation/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json();
        if (res.ok || json.success) {
          setDockets(prev => prev.filter(d => d._id !== id));
          toast.success('Docket deleted successfully');
        } else {
          toast.error(json.message || 'Failed to delete');
        }
      } catch (err) {
        console.error(err);
        toast.error('Delete operation failed');
      }
    }
  };

  // ────── OPEN EDIT FORM ──────
  const handleEdit = (docket) => {
    setEditDocket(docket);
    setFormData({
      docket: docket.docket || '',
      referenceno: docket.referenceno || '',
      bookingdate: docket.bookingdate ? new Date(docket.bookingdate).toISOString().split('T')[0] : '',
      pincode: docket.pincode || '',
      isdoxornon: docket.isdoxornon || 0,
      mode: docket.mode || 0,
      noofpiece: docket.noofpiece || 0,
      length: docket.length || 0,
      breadth: docket.breadth || 0,
      height: docket.height || 0,
      actweight: docket.actweight || 0,
      cvalue: docket.cvalue || 0,
      remarks: docket.remarks || '',
      volweight: docket.volweight || 0,
      codapplicable: docket.codapplicable || 0,
      adjweight: docket.adjweight || 0,
      totvolweight: docket.totvolweight || 0,
      billingweight: docket.billingweight || 0,
      finalweight: docket.finalweight || 0,
      priceperkg: docket.priceperkg || 0,
      doccharge: docket.doccharge || 0,
      odacharge: docket.odacharge || 0,
      codcharge: docket.codcharge || 0,
      unloadingcharge: docket.unloadingcharge || 0,
      loadingcharge: docket.loadingcharge || 0,
      othercharge: docket.othercharge || 0,
      flatcharge: docket.flatcharge || 0,
      fovcharge: docket.fovcharge || 0,
      frieghtcharge: docket.frieghtcharge || 0,
      fuelcharge: docket.fuelcharge || 0,
      cgst: docket.cgst || 0,
      sgst: docket.sgst || 0,
      grandtotal: docket.grandtotal || 0,
      docketstatus: docket.docketstatus || 'Pending'
    });

    const volFactor = getVolumeFactor(docket);

    setMeasurements(
      docket.docketmeasurement?.map(m => {
        const l = Number(m.length) || 0;
        const b = Number(m.breadth) || 0;
        const h = Number(m.height) || 0;
        const cubic = l * b * h;
        const calcVol = cubic > 0 ? cubic / volFactor : (Number(m.volume) || 0);
        const calcCft = cubic > 0 ? cubic / 1728 : (Number(m.cft) || 0);
        return {
          ...m,
          volume: calcVol,
          cft: calcCft,
          id: m._id || Math.random().toString(36).substr(2, 9)
        };
      }) || []
    );

    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditDocket(null);
    setFormData({});
    setMeasurements([]);
  };

  // ────── MEASUREMENT HANDLERS ──────
  const addMeasurement = () => {
    setMeasurements(prev => [
      ...prev,
      {
        id: Math.random().toString(36).substr(2, 9),
        docketnumber: formData.docket,
        length: 0,
        breadth: 0,
        height: 0,
        volume: 0,
        cft: 0,
        wt: 0,
        nos: 0,
        twt: 0
      }
    ]);
  };

  const removeMeasurement = (id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
  };

  const updateMeasurement = (id, field, value) => {
    const volFactor = getVolumeFactor(editDocket);
    setMeasurements(prev =>
      prev.map(m => {
        if (m.id === id) {
          const updated = { ...m, [field]: Number(value) || 0 };
          const cubic = updated.length * updated.breadth * updated.height;
          updated.volume = cubic > 0 ? cubic / volFactor : 0;
          updated.cft = cubic > 0 ? cubic / 1728 : 0;
          updated.twt = updated.wt * updated.nos;
          return updated;
        }
        return m;
      })
    );
  };

  // ────── SUBMIT UPDATED DOCKET ──────
  const handleSubmit = async () => {
    if (!editDocket?._id) return;

    const payload = {
      ...formData,
      docketmeasurement: measurements.map(m => {
        const { id, ...rest } = m;
        return rest;
      })
    };

    try {
      const res = await fetch(`${API_BASE}/onlinebookings/docketconfirmation/${editDocket._id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.success) {
        toast.success('Docket updated successfully');
        await fetchDockets();
        handleCloseForm();
      } else {
        toast.error(json.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error during update');
    }
  };

  // ────── FILTERING & PAGINATION ──────
  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return dockets.filter(d => {
      const matchesSearch =
        !s ||
        (d.docket || '').toLowerCase().includes(s) ||
        (d.referenceno || '').toLowerCase().includes(s) ||
        (d.consigneeid?.name || '').toLowerCase().includes(s) ||
        (d.consignorid?.name || '').toLowerCase().includes(s);

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'COMPLETED' && d.docketstatus === 'Completed') ||
        (statusFilter === 'PENDING' && d.docketstatus !== 'Completed');

      const matchesMode =
        modeFilter === 'ALL' ||
        String(d.mode) === String(modeFilter);

      return matchesSearch && matchesStatus && matchesMode;
    });
  }, [dockets, searchTerm, statusFilter, modeFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setModeFilter('ALL');
    setCurrentPage(1);
  };

  // KPI Calculations
  const totalDockets = dockets.length;
  const completedCount = dockets.filter(d => d.docketstatus === 'Completed').length;
  const pendingCount = totalDockets - completedCount;
  const totalPieces = dockets.reduce((sum, d) => sum + (Number(d.noofpiece) || 0), 0);

  // ────── EXCEL EXPORT ──────
  const exportToExcel = () => {
    if (dockets.length === 0) {
      toast.warn('No dockets to export');
      return;
    }

    const exportData = filtered.map((d, i) => ({
      'S.No': i + 1,
      'Docket No': d.docket || '',
      'Reference No': d.referenceno || '',
      'Consignee': d.consigneeid?.name || '',
      'Origin': getOriginLocation(d),
      'Destination': getDestLocation(d),
      'Mode': getModeLabel(d.mode),
      'Pieces': d.noofpiece || 0,
      'Actual Weight (Kg)': d.actweight || 0,
      'Billing Weight (Kg)': d.billingweight || 0,
      'Grand Total (₹)': d.grandtotal || 0,
      'Status': d.docketstatus || 'Pending',
      'Booking Date': d.bookingdate ? new Date(d.bookingdate).toLocaleDateString('en-GB') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Docket Confirmation');
    XLSX.writeFile(workbook, `Docket_Confirmation_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Docket confirmations exported to Excel!');
  };

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Title & Breadcrumb Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <HiOutlineDocumentText className="text-blue-600 text-3xl" />
            Docket Confirmation Master
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Review booked consignments, update measurements, verify tariff charges, and print consignment notes
          </p>
        </div>

        <button
          onClick={fetchDockets}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer w-fit"
        >
          <MdRefresh size={20} /> Refresh Data
        </button>
      </div>

      {/* KPI Summary Quick Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineDocumentText />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Dockets</p>
            <p className="text-2xl font-extrabold text-gray-900">{totalDockets}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-2xl font-bold">
            <MdCheckCircle />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Completed</p>
            <p className="text-2xl font-extrabold text-green-600">{completedCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center text-2xl font-bold">
            <MdPendingActions />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pending Review</p>
            <p className="text-2xl font-extrabold text-amber-600">{pendingCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl font-bold">
            <FaBoxes />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Pieces</p>
            <p className="text-2xl font-extrabold text-indigo-600">{totalPieces}</p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6">
        {/* Unified Control Toolbar */}
        <div className="flex flex-col gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-lg">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search Docket No, Ref No, Consignee..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <MdClear size={18} />
                </button>
              )}
            </div>

            {/* View Switcher, Entries Per Page & Excel Export */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Entries per page */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="py-2 px-2.5 bg-gray-50 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={6}>6 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'card'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Landscape Card View"
                >
                  <HiOutlineViewGrid size={18} />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'table'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table List View"
                >
                  <HiOutlineViewList size={18} />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>

              {/* Export Excel Button */}
              <button
                onClick={exportToExcel}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                title="Export Dockets to Excel"
              >
                <MdFileDownload size={18} />
                <span className="hidden sm:inline">Export Excel</span>
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <span className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
              <MdFilterList /> Filters:
            </span>

            {/* Filter by Status */}
            <select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
            </select>

            {/* Filter by Mode */}
            <select
              value={modeFilter}
              onChange={e => {
                setModeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Modes</option>
              <option value="1">Surface</option>
              <option value="2">Air</option>
              <option value="3">Train</option>
            </select>

            {(searchTerm || statusFilter !== 'ALL' || modeFilter !== 'ALL') && (
              <button
                onClick={resetFilters}
                className="py-1.5 px-3 text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
              >
                <MdRefresh /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
            <p className="text-sm font-medium">Loading docket confirmations...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <HiOutlineDocumentText className="mx-auto text-5xl text-gray-300 mb-2" />
            <p className="text-gray-700 font-bold text-base">No docket confirmations found</p>
            <p className="text-gray-500 text-xs mt-1">Try adjusting your search criteria or refresh data.</p>
          </div>
        ) : viewMode === 'card' ? (
          /* LANDSCAPE CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-5">
            {paginated.map(d => {
              const origin = getOriginLocation(d);
              const dest = getDestLocation(d);
              const modeLabel = getModeLabel(d.mode);
              const isCompleted = d.docketstatus === 'Completed';

              return (
                <div
                  key={d._id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50/40 px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                        <MdLocalShipping size={20} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-extrabold text-blue-700 truncate tracking-wide">
                          {d.docket || 'NO DOCKET NO'}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-500">
                            Ref: {d.referenceno || '-'}
                          </span>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              isCompleted
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {d.docketstatus?.toUpperCase() || 'PENDING'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setViewDocket(d)}
                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition"
                        title="View Full Details"
                      >
                        <MdVisibility size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(d)}
                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-white rounded-lg transition"
                        title="Edit Docket"
                      >
                        <MdEdit size={18} />
                      </button>
                      <button
                        onClick={() => generateDocketPDF(d, getVolumeFactor(d), customerPrices)}
                        className="p-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-white rounded-lg transition"
                        title="Print Consignment Note (PDF)"
                      >
                        <FaFilePdf size={16} />
                      </button>
                      {isSuperAdmin() && (
                        <button
                          onClick={() => handleDelete(d._id, d.docket)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-white rounded-lg transition"
                          title="Delete Docket"
                        >
                          <MdDelete size={18} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-4 flex-1">
                    {/* Consignee information */}
                    <div>
                      <p className="text-[10px] font-bold uppercase text-gray-400">Consignee</p>
                      <p className="text-sm font-bold text-gray-800 truncate">
                        {d.consigneeid?.name || 'N/A'}
                      </p>
                      {d.consigneeid?.email && (
                        <p className="text-xs text-gray-500 truncate">{d.consigneeid.email}</p>
                      )}
                    </div>

                    {/* Route Path visual */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 flex items-center justify-between gap-3 text-xs">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase text-gray-400">Origin</p>
                        <p className="font-bold text-gray-800 truncate" title={origin}>
                          {origin}
                        </p>
                      </div>

                      <div className="flex flex-col items-center justify-center shrink-0 px-2 text-blue-600">
                        <FaExchangeAlt size={12} className="text-blue-500" />
                        <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.2 rounded mt-1">
                          {modeLabel}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0 text-right">
                        <p className="text-[10px] font-bold uppercase text-gray-400">Destination</p>
                        <p className="font-bold text-gray-800 truncate" title={dest}>
                          {dest}
                        </p>
                      </div>
                    </div>

                    {/* Metric highlights */}
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div className="bg-blue-50 p-2 rounded-lg border border-blue-100">
                        <p className="text-[10px] uppercase font-bold text-blue-700">Pieces</p>
                        <p className="text-sm font-extrabold text-blue-800 mt-0.5">
                          {d.noofpiece || 0}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-2 rounded-lg border border-purple-100">
                        <p className="text-[10px] uppercase font-bold text-purple-700">Actual Wt</p>
                        <p className="text-sm font-extrabold text-purple-800 mt-0.5">
                          {d.actweight || 0} kg
                        </p>
                      </div>
                      <div className="bg-indigo-50 p-2 rounded-lg border border-indigo-100">
                        <p className="text-[10px] uppercase font-bold text-indigo-700">Billing Wt</p>
                        <p className="text-sm font-extrabold text-indigo-800 mt-0.5">
                          {d.billingweight || d.finalweight || 0} kg
                        </p>
                      </div>
                      <div className="bg-emerald-50 p-2 rounded-lg border border-emerald-100">
                        <p className="text-[10px] uppercase font-bold text-emerald-700">Grand Total</p>
                        <p className="text-sm font-extrabold text-emerald-800 mt-0.5">
                          ₹{d.grandtotal || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      Booking:{' '}
                      {d.bookingdate ? new Date(d.bookingdate).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                    <button
                      onClick={() => setViewDocket(d)}
                      className="text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                    >
                      View Breakdown &rarr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* TABLE LIST VIEW */
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white uppercase text-xs font-semibold tracking-wider">
                <tr>
                  <th className="p-3.5 text-left">S.No</th>
                  <th className="p-3.5 text-left">Docket Info</th>
                  <th className="p-3.5 text-left">Consignee</th>
                  <th className="p-3.5 text-left">Route (Origin → Dest)</th>
                  <th className="p-3.5 text-left">Mode</th>
                  <th className="p-3.5 text-left">Pieces / Weight</th>
                  <th className="p-3.5 text-left">Grand Total</th>
                  <th className="p-3.5 text-left">Status</th>
                  <th className="p-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-xs">
                {paginated.map((d, i) => {
                  const sNo = (currentPage - 1) * pageSize + i + 1;
                  const isCompleted = d.docketstatus === 'Completed';

                  return (
                    <tr key={d._id} className="hover:bg-blue-50/50 transition">
                      <td className="p-3.5 font-bold text-gray-500">{sNo}</td>
                      <td className="p-3.5">
                        <div className="flex flex-col">
                          <span className="font-extrabold text-blue-700 text-sm">
                            {d.docket || 'N/A'}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            Ref: {d.referenceno || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-900">
                        {d.consigneeid?.name || 'N/A'}
                      </td>
                      <td className="p-3.5 text-gray-700 font-medium">
                        {getOriginLocation(d)} → {getDestLocation(d)}
                      </td>
                      <td className="p-3.5">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                          {getModeLabel(d.mode)}
                        </span>
                      </td>
                      <td className="p-3.5">
                        <span className="font-bold text-gray-800">{d.noofpiece || 0} Pcs</span>
                        <span className="text-gray-400 block text-[10px]">
                          {d.actweight || 0} kg actual
                        </span>
                      </td>
                      <td className="p-3.5 font-extrabold text-emerald-600 text-sm">
                        ₹{d.grandtotal || 0}
                      </td>
                      <td className="p-3.5">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            isCompleted
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {d.docketstatus?.toUpperCase() || 'PENDING'}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => setViewDocket(d)}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Full Details"
                          >
                            <MdVisibility size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(d)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                            title="Edit Docket"
                          >
                            <MdEdit size={18} />
                          </button>
                          <button
                            onClick={() => generateDocketPDF(d, getVolumeFactor(d), customerPrices)}
                            className="p-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition"
                            title="Print Consignment Note"
                          >
                            <FaFilePdf size={16} />
                          </button>
                          {isSuperAdmin() && (
                            <button
                              onClick={() => handleDelete(d._id, d.docket)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                              title="Delete Docket"
                            >
                              <MdDelete size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 text-xs text-gray-600">
            <div>
              Showing{' '}
              <span className="font-bold text-gray-800">
                {(currentPage - 1) * pageSize + 1}
              </span>{' '}
              to{' '}
              <span className="font-bold text-gray-800">
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-bold text-gray-800">{filtered.length}</span> entries
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const prevVal = arr[idx - 1];
                  return (
                    <React.Fragment key={p}>
                      {prevVal && p - prevVal > 1 && <span className="px-1 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer ${
                          currentPage === p
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* VIEW DOCKET DETAILS MODAL */}
      {viewDocket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-bold text-lg">
                  <MdLocalShipping />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-white">
                    Docket #{viewDocket.docket}
                  </h3>
                  <p className="text-xs text-blue-100">
                    Consignment details, route, weights & billing summary
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewDocket(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5 text-xs">
              {/* Status and Ref */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Reference No</span>
                  <span className="font-bold text-gray-800">{viewDocket.referenceno || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Mode</span>
                  <span className="font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded uppercase">
                    {getModeLabel(viewDocket.mode)}
                  </span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Booking Date</span>
                  <span className="font-bold text-gray-800">
                    {viewDocket.bookingdate ? new Date(viewDocket.bookingdate).toLocaleDateString('en-GB') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Status</span>
                  <span
                    className={`font-bold px-2.5 py-0.5 rounded-full ${
                      viewDocket.docketstatus === 'Completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {viewDocket.docketstatus?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>

              {/* Route & Consignee */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                  <p className="text-xs font-bold text-blue-800 uppercase mb-2">Consignee & Destination</p>
                  <p className="font-extrabold text-gray-900 text-sm">{viewDocket.consigneeid?.name || 'N/A'}</p>
                  <p className="text-gray-600 mt-1">Destination: {getDestLocation(viewDocket)}</p>
                  <p className="text-gray-600">Pincode: {viewDocket.pincode || 'N/A'}</p>
                </div>

                <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/40">
                  <p className="text-xs font-bold text-indigo-800 uppercase mb-2">Origin & Consignor</p>
                  <p className="font-extrabold text-gray-900 text-sm">
                    {viewDocket.consignorid?.name || viewDocket.customerbranch?.branchname || 'Branch'}
                  </p>
                  <p className="text-gray-600 mt-1">Origin: {getOriginLocation(viewDocket)}</p>
                  <p className="text-gray-600">Type: {viewDocket.isdoxornon ? 'DOX' : 'NON-DOX'}</p>
                </div>
              </div>

              {/* Weights and Pieces Grid */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-bold text-gray-700 uppercase mb-3 flex items-center gap-1.5">
                  <HiOutlineScale /> Weight & Volume Summary
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">No of Pieces</span>
                    <span className="text-base font-extrabold text-gray-800">{viewDocket.noofpiece || 0}</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Actual Weight</span>
                    <span className="text-base font-extrabold text-gray-800">{viewDocket.actweight || 0} kg</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Volumetric Wt</span>
                    <span className="text-base font-extrabold text-gray-800">{viewDocket.volweight || 0} kg</span>
                  </div>
                  <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-200">
                    <span className="text-[10px] uppercase font-bold text-blue-700 block">Billing Weight</span>
                    <span className="text-base font-extrabold text-blue-800">
                      {viewDocket.billingweight || viewDocket.finalweight || 0} kg
                    </span>
                  </div>
                </div>
              </div>

              {/* Measurements if any */}
              {Array.isArray(viewDocket.docketmeasurement) && viewDocket.docketmeasurement.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-4">
                  <h4 className="font-bold text-gray-700 uppercase mb-2 flex items-center gap-1.5">
                    <FaBoxes /> Package Measurements ({viewDocket.docketmeasurement.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-gray-100 text-gray-600 font-bold">
                        <tr>
                          <th className="p-2">L (cm)</th>
                          <th className="p-2">B (cm)</th>
                          <th className="p-2">H (cm)</th>
                          <th className="p-2">Volume</th>
                          <th className="p-2">CFT</th>
                          <th className="p-2">Unit Wt</th>
                          <th className="p-2">Nos</th>
                          <th className="p-2">Total Wt</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {viewDocket.docketmeasurement.map((m, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="p-2">{m.length}</td>
                            <td className="p-2">{m.breadth}</td>
                            <td className="p-2">{m.height}</td>
                            <td className="p-2">{Number(m.volume || 0).toFixed(2)}</td>
                            <td className="p-2">{Number(m.cft || 0).toFixed(4)}</td>
                            <td className="p-2">{m.wt}</td>
                            <td className="p-2">{m.nos}</td>
                            <td className="p-2 font-bold">{m.twt}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Charges Breakdown */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-bold text-gray-700 uppercase mb-3 flex items-center gap-1.5">
                  <HiOutlineCurrencyRupee /> Tariff & Surcharge Breakdown
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Price / Kg:</span>
                    <span className="font-bold text-gray-800">₹{viewDocket.priceperkg || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Freight:</span>
                    <span className="font-bold text-gray-800">₹{viewDocket.frieghtcharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Doc Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDocket.doccharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">ODA Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDocket.odacharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Fuel Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDocket.fuelcharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Loading/Unloading:</span>
                    <span className="font-bold text-gray-800">
                      ₹{(Number(viewDocket.loadingcharge) || 0) + (Number(viewDocket.unloadingcharge) || 0)}
                    </span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Taxes (CGST+SGST):</span>
                    <span className="font-bold text-gray-800">
                      ₹{(Number(viewDocket.cgst) || 0) + (Number(viewDocket.sgst) || 0)}
                    </span>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                    <span className="text-emerald-700 block text-[10px] uppercase font-bold">Grand Total:</span>
                    <span className="font-extrabold text-emerald-800 text-sm">₹{viewDocket.grandtotal || 0}</span>
                  </div>
                </div>
              </div>

              {viewDocket.remarks && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <span className="font-bold text-amber-800 uppercase block text-[10px]">Remarks:</span>
                  <p className="text-gray-700 italic mt-0.5">{viewDocket.remarks}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => {
                  const doc = viewDocket;
                  setViewDocket(null);
                  handleEdit(doc);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold uppercase transition flex items-center gap-1.5 cursor-pointer"
              >
                <MdEdit size={16} /> Edit Docket
              </button>
              <button
                onClick={() => generateDocketPDF(viewDocket, getVolumeFactor(viewDocket), customerPrices)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold uppercase transition flex items-center gap-1.5 cursor-pointer"
              >
                <FaFilePdf size={14} /> Print PDF
              </button>
              <button
                onClick={() => setViewDocket(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── EDIT MODAL ────── */}
      {showForm && editDocket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 shadow-2xl border border-gray-200 relative">
            <button
              onClick={handleCloseForm}
              className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            >
              <FaTimes size={22} />
            </button>

            <div className="border-b border-gray-200 pb-4 mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MdEdit className="text-blue-600" />
                Edit Docket: <span className="text-blue-600">{editDocket.docket}</span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Update reference, pieces, measurements, surcharges and completion status
              </p>
            </div>

            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6">
              <h4 className="text-xs font-bold text-blue-700 uppercase mb-3">Basic Information</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Docket No</label>
                  <input
                    type="text"
                    value={formData.docket}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Reference No</label>
                  <input
                    type="text"
                    value={formData.referenceno}
                    onChange={e => setFormData(p => ({ ...p, referenceno: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Booking Date</label>
                  <input
                    type="date"
                    value={formData.bookingdate}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={e => setFormData(p => ({ ...p, pincode: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Dox / Non-Dox</label>
                  <select
                    value={formData.isdoxornon}
                    onChange={e => setFormData(p => ({ ...p, isdoxornon: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                  >
                    <option value={0}>NON-DOX</option>
                    <option value={1}>DOX</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Transport Mode</label>
                  <select
                    value={formData.mode}
                    onChange={e => setFormData(p => ({ ...p, mode: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                  >
                    <option value={0}>Air</option>
                    <option value={1}>Surface</option>
                    <option value={2}>Train</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dimensions & Weight */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6">
              <h4 className="text-xs font-bold text-blue-700 uppercase mb-3">Overall Weight & Dimensions</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Pieces</label>
                  <input
                    type="number"
                    value={formData.noofpiece}
                    onChange={e => setFormData(p => ({ ...p, noofpiece: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Length (cm)</label>
                  <input
                    type="number"
                    value={formData.length}
                    onChange={e => setFormData(p => ({ ...p, length: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Breadth (cm)</label>
                  <input
                    type="number"
                    value={formData.breadth}
                    onChange={e => setFormData(p => ({ ...p, breadth: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Height (cm)</label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={e => setFormData(p => ({ ...p, height: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Actual Wt (kg)</label>
                  <input
                    type="number"
                    value={formData.actweight}
                    onChange={e => setFormData(p => ({ ...p, actweight: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white font-bold text-blue-700"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">C Value (₹)</label>
                  <input
                    type="number"
                    value={formData.cvalue}
                    onChange={e => setFormData(p => ({ ...p, cvalue: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Charges Section */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6">
              <h4 className="text-xs font-bold text-blue-700 uppercase mb-3">Tariff & Surcharge Values (₹)</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {[
                  { name: 'priceperkg', label: 'Price / Kg' },
                  { name: 'doccharge', label: 'Doc Charge' },
                  { name: 'odacharge', label: 'ODA Charge' },
                  { name: 'codcharge', label: 'COD Charge' },
                  { name: 'loadingcharge', label: 'Loading' },
                  { name: 'unloadingcharge', label: 'Unloading' },
                  { name: 'fuelcharge', label: 'Fuel Charge' },
                  { name: 'flatcharge', label: 'Flat Charge' },
                  { name: 'fovcharge', label: 'FOV Charge' },
                  { name: 'frieghtcharge', label: 'Freight' },
                  { name: 'othercharge', label: 'Other' },
                  { name: 'cgst', label: 'CGST' },
                  { name: 'sgst', label: 'SGST' },
                  { name: 'grandtotal', label: 'Grand Total', highlight: true },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1 truncate" title={f.label}>
                      {f.label}
                    </label>
                    <input
                      type="number"
                      value={formData[f.name]}
                      onChange={e => setFormData(p => ({ ...p, [f.name]: Number(e.target.value) }))}
                      className={`w-full p-2 border border-gray-300 rounded-lg text-xs ${
                        f.highlight
                          ? 'bg-emerald-50 text-emerald-800 font-extrabold border-emerald-300'
                          : 'bg-white'
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Status & Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Docket Status *</label>
                <select
                  value={formData.docketstatus}
                  onChange={e => setFormData(p => ({ ...p, docketstatus: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-xs bg-white font-bold"
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={e => setFormData(p => ({ ...p, remarks: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-xs bg-white"
                  placeholder="Optional delivery notes or instructions..."
                />
              </div>
            </div>

            {/* Measurements Table */}
            <div className="border border-gray-200 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-xs font-bold text-gray-800 uppercase flex items-center gap-1.5">
                  <FaBoxes /> Package Measurements Breakdown
                </h4>
                <button
                  onClick={addMeasurement}
                  type="button"
                  className="flex items-center gap-1 bg-green-50 text-green-700 hover:bg-green-100 px-3 py-1.5 rounded-lg text-xs font-bold transition border border-green-200 cursor-pointer"
                >
                  <FaPlus size={12} /> Add Row
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-100 text-gray-700 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="p-2">Length (cm)</th>
                      <th className="p-2">Breadth (cm)</th>
                      <th className="p-2">Height (cm)</th>
                      <th className="p-2">Volume</th>
                      <th className="p-2">CFT</th>
                      <th className="p-2">Unit Wt</th>
                      <th className="p-2">Nos</th>
                      <th className="p-2">Total Wt</th>
                      <th className="p-2 text-center w-12">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {measurements.map(m => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="p-1.5">
                          <input
                            type="number"
                            value={m.length}
                            onChange={e => updateMeasurement(m.id, 'length', e.target.value)}
                            className="w-20 p-1 border rounded text-xs bg-white"
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number"
                            value={m.breadth}
                            onChange={e => updateMeasurement(m.id, 'breadth', e.target.value)}
                            className="w-20 p-1 border rounded text-xs bg-white"
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number"
                            value={m.height}
                            onChange={e => updateMeasurement(m.id, 'height', e.target.value)}
                            className="w-20 p-1 border rounded text-xs bg-white"
                          />
                        </td>
                        <td className="p-1.5 font-bold text-gray-700">{m.volume?.toFixed(2)}</td>
                        <td className="p-1.5 font-bold text-gray-700">{m.cft?.toFixed(4)}</td>
                        <td className="p-1.5">
                          <input
                            type="number"
                            value={m.wt}
                            onChange={e => updateMeasurement(m.id, 'wt', e.target.value)}
                            className="w-16 p-1 border rounded text-xs bg-white"
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number"
                            value={m.nos}
                            onChange={e => updateMeasurement(m.id, 'nos', e.target.value)}
                            className="w-16 p-1 border rounded text-xs bg-white"
                          />
                        </td>
                        <td className="p-1.5 font-extrabold text-blue-700">{m.twt?.toFixed(2)}</td>
                        <td className="p-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => removeMeasurement(m.id)}
                            className="text-red-500 hover:text-red-700 p-1 cursor-pointer"
                            title="Remove measurement"
                          >
                            <FaMinus size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {measurements.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center py-4 text-gray-400 italic">
                          No specific package rows added
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {formData.docketstatus === 'Completed' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-6 flex items-center gap-2 text-green-800 text-xs">
                <FaCheckCircle size={16} />
                <span>
                  This docket will be marked as <strong>Completed</strong> upon saving.
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseForm}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-xs uppercase transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase shadow-md transition cursor-pointer"
              >
                Update & Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocketConfirmationPage;